import React, { useState } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Form, 
  Input, 
  Button, 
  Typography, 
  Card, 
  Select, 
  Upload, 
  message,
  Divider,
  Space
} from 'antd';
import { 
  UploadOutlined, 
  PlusOutlined,
  MinusCircleOutlined 
} from '@ant-design/icons';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;


const BlogUpload = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const navigate = useNavigate();
  const formatContent = (content) => {
  // 1. Escape HTML tags to prevent XSS
  const escaped = content
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // 2. Convert line breaks to <br> tags and preserve multiple spaces
  return escaped
    .replace(/\n/g, '<br>')
    .replace(/  /g, ' &nbsp;');
};

  const onFinish = async (values) => {
    console.log('Form values:', values);
    try {
      setLoading(true);
      
      const blogData = {
        ...values,
        content: formatContent(values.content), // Format content to prevent XSS
        image: imageUrl || 'https://plus.unsplash.com/premium_photo-1675329253565-24dfb38a4b59?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDM0fHx8ZW58MHx8fHx8',
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        views: 0,
        likes: 0,
        commentCount: 0,
        featured:true,
        createdAt: serverTimestamp(),
        author: {
          name: "Admin",
          avatar: "https://randomuser.me/api/portraits/men/1.jpg",
          bio: "Healthcare Consultant"
        }
      };

      const docRef = await addDoc(collection(db, "blogPosts"), blogData);
      message.success('Blog post published successfully!');
      form.resetFields();
      setImageUrl('');
      navigate(`/article/${docRef.id}`);
    } catch (error) {
      console.error("Error adding document: ", error);
      message.error('Failed to publish blog post');
    } finally {
      setLoading(false);
    }
  };

  // For demo - in production you would upload to Firebase Storage
  const handleImageUpload = (info) => {
    if (info.file.status === 'done') {
      // In a real app, you would get the URL from Firebase Storage
      setImageUrl('https://images.unsplash.com/photo-1581056771107-24ca5f033842?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80');
      message.success(`${info.file.name} file uploaded successfully`);
    }
  };

  return (
    <div className="blog-upload-container">
      <Card title={<Title level={2}>Create New Blog Post</Title>}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please input the blog title!' }]}
          >
            <Input placeholder="Enter blog title" />
          </Form.Item>

          <Form.Item
            label="Excerpt"
            name="excerpt"
            rules={[{ required: true, message: 'Please input a short excerpt!' }]}
          >
            <TextArea rows={3} placeholder="Brief description of the blog post" />
          </Form.Item>

          <Form.Item
            label="Content"
            name="content"
            rules={[{ required: true, message: 'Please input the blog content!' }]}
          >
            <TextArea rows={8} placeholder="Write your blog content here (supports HTML)" />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Please select a category!' }]}
          >
            <Select placeholder="Select a category">
              <Option value="Accreditation">Accreditation</Option>
              <Option value="Quality Improvement">Quality Improvement</Option>
              <Option value="Documentation">Documentation</Option>
              <Option value="Training">Training</Option>
              <Option value="Infection Control">Infection Control</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Featured Image">
            <Upload
              name="image"
              listType="picture"
              showUploadList={true}
              onChange={handleImageUpload}
              beforeUpload={() => false} // Prevent actual upload in demo
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>

          <Form.Item label="Featured Post">
            <Form.Item name="featured" valuePropName="checked" noStyle>
              <Input type="checkbox" /> Mark as featured
            </Form.Item>
          </Form.Item>

          <Divider orientation="left">Related Articles</Divider>
          
          <Form.List name="relatedArticles">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'id']}
                      rules={[{ required: true, message: 'Missing article ID' }]}
                    >
                      <Input placeholder="Article ID" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      rules={[{ required: true, message: 'Missing article title' }]}
                    >
                      <Input placeholder="Article Title" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Related Article
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Publish Blog Post
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default BlogUpload;